import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AgGridReact } from 'ag-grid-react';
import { ColDef, AllCommunityModule } from 'ag-grid-community';
import { ModuleRegistry, themeBalham } from 'ag-grid-community';
import { format } from 'date-fns';
import { ILog } from '../../../utils/types/ILog';
import LogDetails from '../../modals/details/LogDetails';
import * as XLSX from 'xlsx';

ModuleRegistry.registerModules([AllCommunityModule]);

interface LogGridProps {
    logs: ILog[];
}

const LogGrid: React.FC<LogGridProps> = ({ logs }) => {
    const gridRef = useRef<AgGridReact>(null);

    const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const [columnDefs] = useState<ColDef<ILog>[]>([
        {
            cellRenderer: (params: any) => <LogDetails log={params.data} />,
            pinned: isMobile ? undefined : 'left',
            headerName: 'Détails',
            sortable: false,
            filter: false,
        },
        { field: 'message', headerName: 'Message', sortable: true, filter: true },
        { field: 'messageTemplate', headerName: 'Template', sortable: true, filter: true },
        { field: 'level', headerName: 'Niveau', sortable: true, filter: true },
        {
            field: 'timeStamp',
            headerName: 'Date',
            sortable: true,
            valueFormatter: (params) => (params.value ? format(new Date(params.value), 'dd/MM/yyyy HH:mm:ss') : ''),
            filter: 'agDateColumnFilter',
        },
        { field: 'exception', headerName: 'Exception', sortable: true, filter: true },
        { field: 'properties', headerName: 'Propriétés', sortable: true, filter: true },
    ]);

    const myTheme = themeBalham.withParams({
        columnBorder: { style: 'dashed', width: '1px' },
    });

    const exportAsCsv = useCallback(() => {
        if (!gridRef.current) return;

        const visibleColumns = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleColumns || visibleColumns.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const columnKeys = visibleColumns.map((col) => col.getColId());
        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');

        gridRef.current.api.exportDataAsCsv({
            fileName: `logs_export_${timestamp}.csv`,
            allColumns: false,
            columnKeys,
            suppressQuotes: false,
        });
    }, []);

    const exportAsExcel = useCallback(() => {
        if (!gridRef.current) return;

        const visibleColumns = gridRef.current.api.getColumns()?.filter((col) => !col.getColDef().hide);
        if (!visibleColumns || visibleColumns.length === 0) {
            alert('Aucune colonne visible à exporter');
            return;
        }

        const rowData: ILog[] = [];
        gridRef.current.api.forEachNodeAfterFilterAndSort((node) => rowData.push(node.data));
        if (rowData.length === 0) {
            alert('Aucune donnée à exporter');
            return;
        }

        // Construire exportData avec uniquement colonnes visibles
        const exportData = rowData.map((row) => {
            const rowObj: Record<string, any> = {};
            visibleColumns.forEach((col) => {
                const colDef = col.getColDef();
                const field = colDef.field;
                const header = colDef.headerName ?? field ?? '';
                if (!field) return;

                let value = (row as any)[field];
                if (field === 'timeStamp') {
                    value = value ? format(new Date(value), 'dd/MM/yyyy HH:mm:ss') : '';
                }
                rowObj[header] = value;
            });
            return rowObj;
        });

        const worksheet = XLSX.utils.json_to_sheet(exportData);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Logs');

        const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
        XLSX.writeFile(workbook, `logs_export_${timestamp}.xlsx`);
    }, []);

    return (
        <>
            <div className="" style={{ height: '100vh', width: '100%' }}>
                <div style={{ height: '100%', width: '100%' }}>
                    <AgGridReact
                        ref={gridRef}
                        theme={myTheme}
                        getRowId={(params) => params.data.id.toString()}
                        rowData={logs}
                        columnDefs={columnDefs}
                        domLayout="normal"
                        defaultColDef={{
                            resizable: true,
                            editable: false,
                            minWidth: 100,
                            flex: 1,
                        }}
                        suppressExcelExport={true}
                        enableCellTextSelection={true}
                        ensureDomOrder={true}
                        animateRows={true}
                        overlayNoRowsTemplate="<span style='padding: 10px;'>Aucune donnée ...</span>"
                    />
                </div>
            </div>

            <div className="dropdown mt-1">
                <button className="btn dropdown-toggle" type="button" id="exportDropdown" data-bs-toggle="dropdown">
                    Export
                </button>
                <ul className="dropdown-menu" aria-labelledby="exportDropdown">
                    <li>
                        <button className="dropdown-item" onClick={exportAsCsv}>
                            Export as CSV
                        </button>
                    </li>
                    <li>
                        <button className="dropdown-item" onClick={exportAsExcel}>
                            Export as Excel
                        </button>
                    </li>
                </ul>
            </div>
        </>
    );
};

export default LogGrid;
